import { DOC_TYPE_INTERNAL, DocTypeEnumType } from '@europrocurement/l2d-domain';
import React, { ChangeEvent, DragEvent, PropsWithChildren, useId } from 'react';

export type UploadableDocumentProps = {
    accept?: string;
    maxMbSize?: number;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onDragEnter: () => void;
    onDragLeave: () => void;
    onValidationError: (err: Error) => void;
    onChange: (files: FileList) => void;
    fileInputRef: React.RefObject<HTMLLabelElement>;
    typeDoc?: DocTypeEnumType;
    isSubmitting?: boolean;
};

const UploadableDocument: React.FunctionComponent<PropsWithChildren<UploadableDocumentProps>> =
    function (props) {
        const {
            children,
            accept = 'application/pdf',
            maxMbSize = 100,
            onMouseEnter,
            onMouseLeave,
            onDragEnter,
            onDragLeave,
            onValidationError,
            onChange,
            fileInputRef,
            typeDoc,
            isSubmitting = false,
        } = props;
        const id = useId();

        const stopDefaults = (
            e: ChangeEvent | DragEvent | MouseEvent | React.MouseEvent<HTMLElement, MouseEvent>,
        ) => {
            e.stopPropagation();
            e.preventDefault();
        };

        const validateFiles = async (files: FileList) =>
            new Promise((resolve, reject) => {
                Array.from(files).forEach((file) => {
                    if (maxMbSize !== undefined && file.size > maxMbSize * 1000000) {
                        reject(new Error('Fichier trop lourd'));
                    }
                    // For .msg file
                    if (typeDoc === DOC_TYPE_INTERNAL && file.type === '') {
                        if (!file.name.includes('.msg')) {
                            reject(new Error('Format de fichier non compatible'));
                        }
                    } else if (accept !== undefined && !accept.split(',').includes(file.type)) {
                        reject(new Error('Format de fichier non compatible'));
                    }
                });

                resolve(files);
            });

        const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
            stopDefaults(event);
            validateFiles(event.target.files || new FileList())
                .then((files: FileList) => onChange(files))
                .catch((err) => onValidationError(err));
        };
        const handleMouseEnter = () => onMouseEnter();
        const handleMouseLeave = () => onMouseLeave();
        const handleDragEnter = () => onDragEnter();
        const handleDragLeave = () => onDragLeave();

        const handleDrop = (event: DragEvent<HTMLElement>) => {
            stopDefaults(event);
            onMouseLeave();
            onDragLeave();

            validateFiles(event.dataTransfer.files || new FileList())
                .then((files: FileList) => onChange(files))
                .catch((err) => onValidationError(err));
        };

        const dragEvents = {
            onMouseEnter: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                stopDefaults(e);
                handleMouseEnter();
            },
            onMouseLeave: (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                stopDefaults(e);
                handleMouseLeave();
            },
            onDragEnter: (e: DragEvent) => {
                stopDefaults(e);
                handleDragEnter();
            },
            onDragLeave: (e: DragEvent) => {
                stopDefaults(e);
                handleDragLeave();
            },
            onDragOver: stopDefaults,
            onDrop: (e: DragEvent<HTMLElement>) => {
                handleDrop(e);
            },
        };

        return (
            <label
                htmlFor={id}
                ref={fileInputRef}
                {...dragEvents}
            >
                <input
                    data-testid="uploadable-document-file-input"
                    id={id}
                    type="file"
                    onChange={handleChange}
                    accept={accept}
                    size={maxMbSize * 1000000}
                    multiple
                    hidden
                    disabled={isSubmitting}
                />
                {children}
            </label>
        );
    };

export default UploadableDocument;
